import { styled } from '@mui/material/styles';
import { useState } from 'react';
import { Avatar, IconButton } from '@mui/material';
import {
  QTypography,
  QSearchBar,
  QTable,
  QTableHead,
  QTableBody,
  QTableCell,
  QTableRow,
  QAsyncTablePagination,
  QTableSortableColumnHeader,
  QAppTypeIcon,
} from '@kw/quokka-ui';
import WarningIcon from '@mui/icons-material/WarningAmberRounded';
import DeniedIcon from '@mui/icons-material/GppBad';
import TrustedIcon from '@mui/icons-material/GppGood';
import { Android, Apple, CalendarToday, MoreVert as MoreVertIcon } from '@mui/icons-material';
import dayjs from 'dayjs';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { ApplicationVersion } from '@kw/device-service/dist/application/entities/applicationVersion.entity';
import utc from 'dayjs/plugin/utc';
import { TableRowLink } from '../shared/TableRowLink';
import { PageLoader } from '../../PageLoader';
import { fetchService } from '../../utils/fetchService';
import { useLocalStorage } from '../../utils/useLocalStorage';
import CountriesContactedCellContent from './CountriesContactedCellContent';
import { ApplicationsPopover } from './ApplicationsPopover';
import { useOrganization } from '../Settings/SettingsDropDownMenu';
import { LaunchDarklyFlags } from '../../launch-darkly';
import { SubstituteVersion } from './SubstituteVersion';
import { formatManagementDisplay } from '../../utils/formatManagementDisplay';
import { formatAppSourceDisplay } from '../../utils/formatAppSourceDisplay';
import { getDisplayedAppVersion, isSyntheticBinaryVersion } from './appVersionDisplay';
import { appIconDataUrl } from '../../utils/appIconDataUrl';

interface IProps {
  apps: (ApplicationVersion & {
    applicationVersion: string;
    displayVersion: string;
    icon: string;
    importantFindings: boolean;
    management: string;
    appSource: string;
    policyViolations: boolean;
    policyIds: string[];
    riskIndicatorCount: number;
    title: string;
    versions: { deviceCount: number; deviceIds: string[]; version: string }[];
  })[];
  total: number;
  appsDataError: boolean;
  isLoading: boolean;
  highRiskCountries: string[];
  highRiskCountriesError: boolean;
  searchQuery: string;
  setSearchQuery: React.Dispatch<string>;
  appNameOrder: 'ASC' | 'DESC' | null;
  toggleAppNameOrder: () => void;
  numberOfIorsOrder: 'ASC' | 'DESC' | null;
  toggleNumberOfIorsOrder: () => void;
  dateSubmittedOrder: 'ASC' | 'DESC' | null;
  toggleDateSubmittedOrder: () => void;
  policyViolationsOrder: 'ASC' | 'DESC' | null;
  togglePolicyViolationsOrder: () => void;
  page: number;
  pageSize: number;
  setPage: React.Dispatch<number>;
  handleChangePage: (event: unknown, newPage: number) => void;
  handleChangeRowsPerPage: (option: { label: string; value: number }) => void;
  backQParams: string;
  fetchAppList?: () => void;
}

export default function ApplicationsTable({
  apps,
  total,
  appsDataError,
  isLoading,
  highRiskCountries,
  highRiskCountriesError,
  searchQuery,
  setSearchQuery,
  appNameOrder,
  toggleAppNameOrder,
  dateSubmittedOrder,
  toggleDateSubmittedOrder,
  policyViolationsOrder,
  togglePolicyViolationsOrder,
  page,
  pageSize,
  setPage,
  handleChangeRowsPerPage,
  backQParams,
  fetchAppList,
}: IProps) {
  const [accessToken = ''] = useLocalStorage('accessToken', '');
  const { orgData } = useOrganization(accessToken);
  const [popoverId, setPopoverId] = useState(null);
  const [popoverAnchorEl, setPopoverAnchorEl] = useState(null);
  const flags: LaunchDarklyFlags['flags'] = useFlags();
  const [localSearch, setLocalSearch] = useState(searchQuery);

  dayjs.extend(utc);

  if (appsDataError) {
    console.error(appsDataError);
    return <div>failed to load</div>;
  }
  if (isLoading) return <PageLoader />;

  const showDeniedApps = orgData?.settings?.includes('denied-apps');
  const totalPageCount = Math.ceil(total / pageSize);
  const platformIcon = (os: string) => (os === 'ios' ? <StyledAppleIcon /> : <StyledAndroidIcon />);

  const renderAppTag = (isDenied, isAllowed, importantFindings) => {
    if (isDenied) {
      return (
        <span>
          <StyledDeniedIcon color="error" />
          <StyledWarningMessage>Denied App</StyledWarningMessage>
        </span>
      );
    }
    if (isAllowed) {
      return (
        <>
          <StyledTrustedIcon color="success" />
          <StyledTrustedTagText>Trusted App</StyledTrustedTagText>
        </>
      );
    }
    if (importantFindings) {
      return (
        <span>
          <StyledWarningIcon color="error" />
          <StyledWarningMessage>Important Findings</StyledWarningMessage>
        </span>
      );
    }
    return null;
  };

  if (appsDataError) {
    console.error(appsDataError);
    return <div>failed to load</div>;
  }

  return (
    <div>
      <QSearchBar
        searchValue={localSearch}
        onChange={e => setLocalSearch(e.target.value)}
        onKeyPress={e => {
          if (e.key === 'Enter') {
            setSearchQuery(localSearch);
            setPage(0);
          }
        }}
        onRemove={() => {
          setLocalSearch('');
          setSearchQuery('');
          setPage(0);
        }}
        placeholder="Search applications"
        onButtonSearch={() => {
          setSearchQuery(localSearch);
          setPage(0);
        }}
      />

      <QAsyncTablePagination
        page={page + 1}
        pagination={{
          dataPerPage: pageSize,
          totalData: total,
          lastPage: totalPageCount,
        }}
        getPage={newPage => setPage(newPage - 1)}
        showRowsPerPage={true}
        currentRows={apps.length}
        handlePageSizeChange={({ value }) => {
          setPage(0);
          handleChangeRowsPerPage({ label: String(value), value });
        }}
        pageSize={pageSize}
        pageSizeOptions={[10, 20, 50, 100].map(v => ({ label: v, value: v }))}
      />

      <QTable aria-label="Applications-Table">
        <QTableHead>
          <QTableRow>
            {[
              { title: 'App Name', onClick: toggleAppNameOrder, sortValue: appNameOrder },
              { title: 'App Type' },
              { title: 'App Source' },
              { title: 'Date Submitted', onClick: toggleDateSubmittedOrder, sortValue: dateSubmittedOrder },
              { title: 'Devices' },
              { title: 'Countries Contacted' },
              { title: 'Policy Violations', onClick: togglePolicyViolationsOrder, sortValue: policyViolationsOrder },
              { title: 'Actions' },
            ].map(({ title, onClick, sortValue }) =>
              onClick ? (
                <QTableSortableColumnHeader
                  key={title}
                  title={title}
                  onClick={e => {
                    e.stopPropagation();
                    onClick();
                    setPage(0);
                  }}
                  sortValue={sortValue ?? null}
                />
              ) : (
                <QTableCell key={title}>{title}</QTableCell>
              ),
            )}
          </QTableRow>
        </QTableHead>

        <QTableBody>
          {apps.map(app => {
            const {
              applicationId,
              applicationVersion,
              displayVersion,
              analyzedVersion,
              deviceCountVersion,
              appIconBase64,
              importantFindings,
              management,
              appSource,
              os,
              policyViolations,
              policyIds,
              riskIndicatorCount,
              title,
              updatedDate,
              isDenied,
              isAllowed,
              installedVersion,
              analysisId,
              dateSubmitted,
              status,
              countriesContactedCodesSarif,
            } = app as any;

            const localIconUrl = appIconDataUrl(appIconBase64);
            const reportPath = `/applications/app-detail-report?analysisId=${analysisId}&backQParams=${encodeURIComponent(backQParams)}`;

            return (
              <TableRowLink
                key={`${applicationId}${installedVersion}${os}${riskIndicatorCount}`}
                ariaLabel={`${title} row`}
                uniqueId={applicationId}
                href={status === 'FINISHED' ? reportPath : null}
              >
                <StyledApplicationsTableCell selected={!!appNameOrder}>
                  <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                    {appIconBase64 ? (
                      <Avatar alt="icon" src={localIconUrl} sx={{ width: 24, height: 24, marginRight: 1 }} />
                    ) : (
                      <img src="/icons/app-placeholder-60x60.svg" alt="placeholder" width={24} height={24} style={{ marginRight: 10 }} />
                    )}
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <div>
                        <StyledAppTitle>{decodeURIComponent(title)}</StyledAppTitle>
                        {renderAppTag(isDenied, isAllowed, importantFindings)}
                      </div>
                      <div>{applicationId}</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <StyledIconWrapper>{platformIcon(os)}</StyledIconWrapper>
                        {/* Uploaded binaries show their clean displayVersion; managed apps with installed version 0 show the
                            analyzed version instead. No substitution icon for binaries or such managed apps. */}
                        {getDisplayedAppVersion({ installedVersion, analyzedVersion, displayVersion })}
                        {!isSyntheticBinaryVersion(installedVersion) && installedVersion !== '0' && (
                          <SubstituteVersion analyzedVersion={analyzedVersion} installedVersion={installedVersion} />
                        )}
                      </div>
                    </div>
                  </div>
                </StyledApplicationsTableCell>
                <StyledApplicationsTableCell>
                  <StyledAppTypeCell>
                    <QAppTypeIcon appType={formatManagementDisplay(management)} />
                    {formatManagementDisplay(management)}
                  </StyledAppTypeCell>
                </StyledApplicationsTableCell>
                <StyledApplicationsTableCell>{formatAppSourceDisplay(appSource)}</StyledApplicationsTableCell>
                <StyledApplicationsTableCell>
                  <StyledDateSubmittedCell>
                    <CalendarToday sx={{ fontSize: 12, color: '#244c5a' }} />
                    {dayjs.utc(dateSubmitted).local().format('MM/DD/YY')}
                  </StyledDateSubmittedCell>
                </StyledApplicationsTableCell>
                <StyledApplicationsTableCell>{deviceCountVersion}</StyledApplicationsTableCell>
                {status === 'FINISHED' ? (
                  <>
                    <StyledApplicationsTableCell>
                      <CountriesContactedCellContent
                        contactedCountryCodes={countriesContactedCodesSarif}
                        highRiskCountries={highRiskCountries}
                        highRiskCountriesError={highRiskCountriesError}
                      />
                    </StyledApplicationsTableCell>
                    <StyledApplicationsTableCell>
                      {policyIds.length > 0 ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <QTypography color="error">{policyIds.length}</QTypography>
                          <WarningIcon fontSize="small" color="error" />
                        </div>
                      ) : (
                        '0'
                      )}
                    </StyledApplicationsTableCell>
                  </>
                ) : (
                  <StyledApplicationsTableCell colSpan={2}>
                    <QTypography variant="bodyRegular" color="#3B3E40">
                      {status === 'NOT_STARTED'
                        ? 'Not started'
                        : status === 'FAILED'
                        ? 'Analysis unavailable'
                        : 'Analysis in progress, this may take a while...'}
                    </QTypography>
                  </StyledApplicationsTableCell>
                )}
                <StyledApplicationsTableCell>
                  <StyledActionsButton
                    onClick={e => {
                      e.stopPropagation();
                      e.preventDefault();
                      setPopoverId(applicationId);
                      setPopoverAnchorEl(e.currentTarget);
                    }}
                  >
                    <MoreVertIcon />
                  </StyledActionsButton>
                  <ApplicationsPopover
                    applicationId={applicationId}
                    applicationVersion={applicationVersion}
                    os={os}
                    name={title}
                    isOpen={popoverId === applicationId}
                    anchorEl={popoverAnchorEl}
                    handleClose={() => {
                      setPopoverId(null);
                      setPopoverAnchorEl(null);
                    }}
                    fetchDeniedApps={fetchAppList}
                    fetchTrustedApps={fetchAppList}
                    showDeniedApps={showDeniedApps}
                    disableOptions={status !== 'FINISHED'}
                    reportPath={reportPath}
                    isDenied={isDenied}
                    isTrusted={isAllowed}
                  />
                </StyledApplicationsTableCell>
              </TableRowLink>
            );
          })}
        </QTableBody>
      </QTable>

      <QAsyncTablePagination
        page={page + 1}
        pagination={{
          dataPerPage: pageSize,
          totalData: total,
          lastPage: totalPageCount,
        }}
        getPage={newPage => setPage(newPage - 1)}
        showRowsPerPage={true}
        currentRows={apps.length}
        handlePageSizeChange={({ value }) => {
          setPage(0);
          handleChangeRowsPerPage({ label: String(value), value });
        }}
        pageSize={pageSize}
        pageSizeOptions={[10, 20, 50, 100].map(v => ({ label: v, value: v }))}
      />
    </div>
  );
}

const StyledWarningIcon = styled(WarningIcon)`
  font-size: 21px;
  margin: -6px 4px;
`;

const StyledDeniedIcon = styled(DeniedIcon)`
  font-size: 21px;
  margin: -6px 4px;
`;

const StyledTrustedIcon = styled(TrustedIcon)`
  font-size: 21px;
  margin: -6px 4px;
`;

const StyledTrustedTagText = styled('span')`
  color: #4caf50;
`;

const StyledWarningMessage = styled('span')`
  color: #d32f2f;
`;

const StyledIconWrapper = styled('div')`
  display: inline-block;
  margin-right: 5px;
`;

const StyledAppleIcon = styled(Apple)`
  font-size: 12px;
  color: #a2aaad;
`;

const StyledAndroidIcon = styled(Android)`
  font-size: 12px;
  color: #a4c639;
`;

const StyledAppTypeCell = styled('div')`
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
`;

const StyledDateSubmittedCell = styled('div')`
  display: flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
`;

const StyledApplicationsTableCell = styled(QTableCell)`
  vertical-align: top;
`;

const StyledActionsButton = styled(IconButton)`
  padding: 0 8px;
`;

const StyledAppTitle = styled('div')`
  font-size: 14px;
  line-height: 21px;
  font-weight: 400;
  display: inline-block;
`;
